import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Wrench } from "lucide-react";

/**
 * QuickSpecsTabs — configurable model comparison with interactive tabs.
 *
 * Two layouts (auto-selected):
 *  A. Split layout (when `quickSpecs` prop provided):
 *     Left: 4 QUICK_SPEC summary cards
 *     Right: interactive model tabs with spec grid
 *     Header left-aligned.
 *  B. Centered layout (no `quickSpecs`):
 *     Header centered, model tabs in max-w-4xl centered card.
 *
 * Props:
 *  - testId
 *  - eyebrow (default "At a Glance")
 *  - heading (ReactNode)
 *  - subtitle
 *  - quickSpecs?: { label, value }[]   (4 summary cards, optional)
 *  - models: { [key]: { name, tagline, rows: {label, value}[] } }
 *  - defaultModel: string (first model key by default)
 *  - footerNote?: string (italic disclaimer below)
 */
export default function QuickSpecsTabs({
  testId,
  eyebrow = "At a Glance",
  heading,
  subtitle,
  quickSpecs,
  models,
  defaultModel,
  footerNote = "* Specifications are subject to continuous product improvement without prior notice.",
}) {
  const modelKeys = Object.keys(models);
  const [activeModel, setActiveModel] = useState(
    defaultModel || modelKeys[0]
  );
  const model = models[activeModel];
  const isSplit = Array.isArray(quickSpecs) && quickSpecs.length > 0;

  return (
    <section
      data-testid={testId}
      className="relative bg-liftile-black py-24 md:py-32 overflow-hidden"
    >
      <div
        aria-hidden
        className="absolute top-0 left-0 right-0 h-px bg-liftile-orange/30"
      />
      <div
        aria-hidden
        className="absolute top-1/2 -left-32 w-[520px] h-[520px] rounded-full pointer-events-none opacity-[0.14] -translate-y-1/2"
        style={{
          background:
            "radial-gradient(circle, rgba(255,107,26,0.55) 0%, rgba(255,107,26,0) 70%)",
          filter: "blur(60px)",
        }}
      />

      <div className="container-x relative">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.6 }}
          className={
            isSplit
              ? "max-w-2xl mb-14 md:mb-16"
              : "max-w-3xl mx-auto text-center mb-14 md:mb-16"
          }
        >
          <div className="heading-eyebrow mb-3">{eyebrow}</div>
          <h2 className="display-2 text-liftile-cream">{heading}</h2>
          {subtitle && (
            <p
              className={`font-body text-liftile-cream/70 leading-relaxed ${
                isSplit
                  ? "mt-5 text-base md:text-lg"
                  : "mt-6 text-base md:text-lg max-w-2xl mx-auto"
              }`}
            >
              {subtitle}
            </p>
          )}
        </motion.div>

        {isSplit ? (
          <div className="grid lg:grid-cols-[1fr_1.2fr] gap-8 md:gap-10 lg:gap-14">
            {/* LEFT — 4 summary spec cards */}
            <motion.div
              initial={{ opacity: 0, y: 22 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.65 }}
              className="grid grid-cols-1 sm:grid-cols-2 gap-5"
            >
              {quickSpecs.map((s, i) => (
                <motion.div
                  key={s.label}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-40px" }}
                  transition={{ duration: 0.55, delay: i * 0.08 }}
                  data-testid={`${testId}-card-${i}`}
                  className="group relative rounded-2xl bg-white/[0.03] border border-white/[0.08] p-7 hover:border-liftile-orange/50 hover:bg-white/[0.05] transition-all duration-500"
                >
                  <span className="absolute top-0 right-0 h-[2px] w-0 bg-liftile-orange transition-all duration-500 group-hover:w-14 rounded-bl-full" />
                  <div className="font-heading text-[11px] uppercase tracking-[0.22em] text-liftile-orange font-semibold">
                    {s.label}
                  </div>
                  <div className="font-display text-2xl md:text-[1.85rem] text-liftile-cream mt-3 leading-tight">
                    {s.value}
                  </div>
                </motion.div>
              ))}
            </motion.div>

            {/* RIGHT — model selector */}
            <ModelSelector
              testId={testId}
              modelKeys={modelKeys}
              activeModel={activeModel}
              setActiveModel={setActiveModel}
              model={model}
              gapClass="p-7 md:p-8"
            />
          </div>
        ) : (
          <ModelSelector
            testId={testId}
            modelKeys={modelKeys}
            activeModel={activeModel}
            setActiveModel={setActiveModel}
            model={model}
            gapClass="p-7 md:p-10"
            className="max-w-4xl mx-auto"
          />
        )}

        {footerNote && (
          <p className="font-body italic text-xs md:text-sm text-liftile-cream/50 text-center mt-6 max-w-2xl mx-auto">
            {footerNote}
          </p>
        )}
      </div>
    </section>
  );
}

function ModelSelector({
  testId,
  modelKeys,
  activeModel,
  setActiveModel,
  model,
  gapClass = "p-7 md:p-10",
  className = "",
}) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 22 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.65, delay: 0.1 }}
      data-testid={`${testId}-selector`}
      className={`rounded-2xl bg-white/[0.04] border border-white/[0.08] overflow-hidden ${className}`}
    >
      {/* Tab pills */}
      <div className="flex border-b border-white/[0.06] p-3 gap-2 bg-black/30">
        {modelKeys.map((key) => {
          const active = key === activeModel;
          return (
            <button
              key={key}
              type="button"
              onClick={() => setActiveModel(key)}
              data-testid={`${testId}-tab-${key}`}
              className={`relative flex-1 font-heading text-xs md:text-sm uppercase tracking-[0.18em] font-semibold px-4 py-3 rounded-xl transition-all duration-400 ${
                active
                  ? "bg-liftile-orange text-white shadow-[0_10px_28px_-10px_rgba(255,107,26,0.8)]"
                  : "text-liftile-cream/70 hover:text-liftile-cream"
              }`}
            >
              {key}
            </button>
          );
        })}
      </div>

      {/* Spec table */}
      <div className={gapClass}>
        <AnimatePresence mode="wait">
          <motion.div
            key={activeModel}
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -6 }}
            transition={{ duration: 0.35, ease: "easeOut" }}
          >
            <div className="flex items-baseline justify-between mb-6">
              <div>
                <div className="font-display text-2xl md:text-[1.75rem] text-liftile-cream leading-tight">
                  {model.name}
                </div>
                <div className="font-body text-xs md:text-sm text-liftile-cream/55 mt-1">
                  {model.tagline}
                </div>
              </div>
              <span className="hidden sm:inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.22em] font-heading text-liftile-orange font-semibold">
                <Wrench className="w-3 h-3" /> Configuration
              </span>
            </div>

            <dl
              data-testid={`${testId}-specs-${activeModel}`}
              className="grid sm:grid-cols-2 gap-x-8"
            >
              {model.rows.map((r) => (
                <div
                  key={r.label}
                  className="flex justify-between items-baseline gap-4 py-3 border-b border-white/[0.06] last:border-0"
                >
                  <dt className="font-heading text-[11px] uppercase tracking-[0.18em] text-liftile-cream/55">
                    {r.label}
                  </dt>
                  <dd className="font-body text-sm md:text-[15px] text-liftile-cream text-right">
                    {r.value}
                  </dd>
                </div>
              ))}
            </dl>
          </motion.div>
        </AnimatePresence>
      </div>
    </motion.div>
  );
}
